

import { useState, useEffect } from "react";
import SearchableDropdown from "../components/SearchableDropdown";
import { mastersApi } from "@/lib/api";

interface VendorFormData {
  name: string;
  vendor_type_id: string;
  contact_person: string;
  phone: string;
  email: string;
  address: string;
  gst_number: string;
  commission_type: string;
  commission_value: string;
  status: string;
}

interface VendorFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  initialData?: Partial<VendorFormData>;
}

const statuses = ["active", "inactive"];
const commissionTypes = ["fixed", "percentage"];

export default function VendorForm({ onSubmit, onCancel, initialData }: VendorFormProps) {
  const [formData, setFormData] = useState<VendorFormData>(() => ({
    name: initialData?.name ?? "",
    vendor_type_id: initialData?.vendor_type_id ?? "",
    contact_person: initialData?.contact_person ?? "",
    phone: initialData?.phone ?? "",
    email: initialData?.email ?? "",
    address: initialData?.address ?? "",
    gst_number: initialData?.gst_number ?? "",
    commission_type: initialData?.commission_type ?? "fixed",
    commission_value: initialData?.commission_value ?? "0",
    status: initialData?.status ?? "active",
  }));

  const [errors, setErrors] = useState<Partial<Record<keyof VendorFormData, string>>>({});
  const [vendorTypes, setVendorTypes] = useState<{id: number; name: string}[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  // Fetch vendor types from API
  useEffect(() => {
    loadVendorTypes();
  }, []);

  const loadVendorTypes = async () => {
    try {
      const response = await mastersApi.vendorTypes.list();
      const types = response.data?.data || response.data || [];
      setVendorTypes(types);
    } catch (error) {
      console.error("Failed to load vendor types:", error);
    } finally {
      setLoadingTypes(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof VendorFormData, string>> = {};
    
    if (!formData.name.trim()) newErrors.name = "Vendor name is required";
    if (!formData.vendor_type_id) newErrors.vendor_type_id = "Vendor type is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.contact_person.trim()) newErrors.contact_person = "Contact person is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Convert commission_value to number
      const submitData = {
        ...formData,
        commission_value: parseFloat(formData.commission_value) || 0,
        vendor_type_id: parseInt(formData.vendor_type_id),
      };
      onSubmit(submitData);
    }
  };

  const handleChange = (field: keyof VendorFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const inputClass = (field: keyof VendorFormData) => `
    w-full px-3 py-2.5 rounded-xl border text-sm transition-colors
    ${errors[field] 
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100" 
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Vendor Name */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Vendor Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Enter vendor/business name"
            className={inputClass("name")}
          />
          {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
        </div>

        {/* Vendor Type */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Vendor Type *</label>
          <select
            value={formData.vendor_type_id}
            onChange={(e) => handleChange("vendor_type_id", e.target.value)}
            className={inputClass("vendor_type_id")}
          >
            <option value="">Select vendor type</option>
            {vendorTypes.map((type) => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
          {errors.vendor_type_id && <p className="text-xs text-rose-500 mt-1">{errors.vendor_type_id}</p>}
        </div>

        {/* Contact Person */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Contact Person *</label>
          <input
            type="text"
            value={formData.contact_person}
            onChange={(e) => handleChange("contact_person", e.target.value)}
            placeholder="Primary contact name"
            className={inputClass("contact_person")}
          />
          {errors.contact_person && <p className="text-xs text-rose-500 mt-1">{errors.contact_person}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Phone Number *</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            placeholder="+91 98765 43210"
            className={inputClass("phone")}
          />
          {errors.phone && <p className="text-xs text-rose-500 mt-1">{errors.phone}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="vendor@example.com"
            className={inputClass("email")}
          />
        </div>

        {/* GST Number */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">GST Number</label>
          <input
            type="text"
            value={formData.gst_number}
            onChange={(e) => handleChange("gst_number", e.target.value)}
            placeholder="22AAAAA0000A1Z5"
            className={inputClass("gst_number")}
          />
        </div>

        {/* Commission Type */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Commission Type</label>
          <select
            value={formData.commission_type}
            onChange={(e) => handleChange("commission_type", e.target.value)}
            className={inputClass("commission_type")}
          >
            {commissionTypes.map(t => (
              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Commission Value */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            Commission {formData.commission_type === "percentage" ? "Percentage (%)" : "Amount (₹)"}
          </label>
          <input
            type="number"
            value={formData.commission_value}
            onChange={(e) => handleChange("commission_value", e.target.value)}
            placeholder={formData.commission_type === "percentage" ? "e.g. 10" : "0"}
            min="0"
            max={formData.commission_type === "percentage" ? "100" : undefined}
            step={formData.commission_type === "percentage" ? "0.01" : "1"}
            className={inputClass("commission_value")}
          />
          {formData.commission_type === "percentage" && (
            <p className="text-[10px] text-stone-400 mt-0.5">Enter 0–100</p>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
          <select
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className={inputClass("status")}
          >
            {statuses.map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Address */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Address</label>
          <textarea
            value={formData.address}
            onChange={(e) => handleChange("address", e.target.value)}
            placeholder="Full address"
            rows={2}
            className={`${inputClass("address")} resize-none`}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-stone-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update Vendor" : "Add Vendor"}
        </button>
      </div>
    </form>
  );
}
